/**
 * Observed lead-time selector (WR4 SR4.3, plan §WR4 SR4.3) — a FREE v2 enabler
 * grafted from the ladder draft. Zero I/O, no globals, no `Date.now`: the caller
 * supplies the purchase-order + goods-receipt facts and the function derives the
 * vendor's recent actual purchase lead time from real PO→GR cycles.
 *
 * v1 does NOT feed this into any formula (Appendix B / Appendix D-3 — no
 * computed lead time, no auto-calc params in v1). It powers ONE read-only chip
 * (實際前置期) on the vendor-line surface and is the capture mechanism the v2
 * ladder rung consumes (`ltMean`/`ltStd` from observed PO→GR pairs — Appendix C).
 * DERIVED-NEVER-STORED: recomputed per read, never written back.
 *
 * Definition of one observation (OQ-8):
 *   orderedAt = PO `sentAt` (sent-to-vendor) PREFERRED, `createdAt` FALLBACK;
 *   receivedAt = the EARLIEST *posted* goods-receipt docket against that PO
 *                (first goods arrival closes the lead-time cycle);
 *   gapDays   = round((receivedAt − orderedAt) / day), clamped ≥ 0.
 * Unreceived POs (no posted docket) are EXCLUDED — an open PO has no lead time
 * yet. Draft dockets never count. The window is the last `LEAD_TIME_WINDOW`
 * observations (newest by `orderedAt`); the summary is 資料不足 (insufficient)
 * until `LEAD_TIME_MIN_OBSERVATIONS` land.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Observations considered = the most-recent this many completed cycles. */
export const LEAD_TIME_WINDOW = 3;

/**
 * Below this many observations the summary is 資料不足 (insufficient) — the chip
 * withholds a mean rather than quoting a lead time from one or two cycles.
 */
export const LEAD_TIME_MIN_OBSERVATIONS = 3;

/** Only 'posted' dockets complete a cycle (draft dockets carry no receipt truth). */
export type LeadTimeReceiptStatus = 'draft' | 'posted';

/** Minimal PO fact the selector needs — provenance-typed, portable to Joe's backend. */
export interface LeadTimePurchaseOrderInput {
  id: string;
  vendorId: number;
  /**
   * Sent-to-vendor stamp (OQ-8 preferred basis). Optional/nullable — today's
   * PO record has no `sentAt`, so the caller passes `null` and the selector
   * falls back to `createdAt`; the preference is honoured the moment Joe's
   * backend supplies `sentAt`.
   */
  sentAt?: string | null;
  createdAt: string;
}

/** Minimal goods-receipt fact — only the poId, its posting status, and the received date. */
export interface LeadTimeGoodsReceiptInput {
  poId: string;
  status: LeadTimeReceiptStatus;
  /** 進貨日 — the received-at stamp that closes the cycle. */
  receiptDate: string;
}

/** One completed PO→GR cycle for a vendor. */
export interface LeadTimeObservation {
  poId: string;
  /** The resolved order stamp (`sentAt` ?? `createdAt`). */
  orderedAt: string;
  /** Earliest posted docket `receiptDate` for the PO. */
  receivedAt: string;
  /** round((receivedAt − orderedAt) / day), clamped ≥ 0. */
  gapDays: number;
}

/** The vendor's observed-lead-time roll-up over the last-`LEAD_TIME_WINDOW` cycles. */
export interface LeadTimeSummary {
  /** The window, newest `orderedAt` first (≤ `LEAD_TIME_WINDOW`). */
  observations: LeadTimeObservation[];
  /** Unweighted mean of the window's `gapDays`; `null` when the window is empty. */
  meanDays: number | null;
  /** Window size (0..`LEAD_TIME_WINDOW`). */
  count: number;
  /** `count >= LEAD_TIME_MIN_OBSERVATIONS` — false renders the 資料不足 tone. */
  sufficient: boolean;
}

/** Parse an ISO stamp to epoch ms, or `null` when unparseable. */
function parseMs(stamp: string | null | undefined): number | null {
  if (!stamp) return null;
  const ms = Date.parse(stamp);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Map each PO id to the EARLIEST posted goods-receipt `receiptDate` (ms). A PO
 * with only draft dockets — or with an unparseable receipt date — never lands a
 * key, so it is excluded downstream (unreceived).
 */
function earliestPostedReceiptByPo(
  goodsReceipts: readonly LeadTimeGoodsReceiptInput[]
): Map<string, number> {
  const byPo = new Map<string, number>();
  for (const gr of goodsReceipts) {
    if (gr.status !== 'posted') continue;
    const ms = parseMs(gr.receiptDate);
    if (ms === null) continue;
    const current = byPo.get(gr.poId);
    if (current === undefined || ms < current) {
      byPo.set(gr.poId, ms);
    }
  }
  return byPo;
}

/**
 * Derive a vendor's observed lead time from completed PO→GR cycles. Pure and
 * deterministic: sorted newest-`orderedAt`-first (poId tie-break), windowed to
 * the last `LEAD_TIME_WINDOW`, mean of that window only.
 */
export function selectLeadTimeObservations(
  vendorId: number,
  purchaseOrders: readonly LeadTimePurchaseOrderInput[],
  goodsReceipts: readonly LeadTimeGoodsReceiptInput[]
): LeadTimeSummary {
  const receivedByPo = earliestPostedReceiptByPo(goodsReceipts);

  const candidates: LeadTimeObservation[] = [];
  for (const po of purchaseOrders) {
    if (po.vendorId !== vendorId) continue;
    const receivedMs = receivedByPo.get(po.id);
    if (receivedMs === undefined) continue; // unreceived — no lead time yet

    const orderedStamp = po.sentAt ?? po.createdAt; // OQ-8: sentAt preferred
    const orderedMs = parseMs(orderedStamp);
    if (orderedMs === null) continue;

    const gapDays = Math.round((receivedMs - orderedMs) / MS_PER_DAY);
    if (gapDays < 0) continue; // receipt before order = bad data, exclude

    candidates.push({
      poId: po.id,
      orderedAt: orderedStamp,
      receivedAt: new Date(receivedMs).toISOString(),
      gapDays,
    });
  }

  candidates.sort((a, b) => {
    const byOrdered = (parseMs(b.orderedAt) ?? 0) - (parseMs(a.orderedAt) ?? 0);
    return byOrdered !== 0 ? byOrdered : b.poId.localeCompare(a.poId);
  });

  const observations = candidates.slice(0, LEAD_TIME_WINDOW);
  const count = observations.length;
  const meanDays =
    count === 0 ? null : observations.reduce((sum, o) => sum + o.gapDays, 0) / count;

  return {
    observations,
    meanDays,
    count,
    sufficient: count >= LEAD_TIME_MIN_OBSERVATIONS,
  };
}
